import { BCMSCurrency, BCMSDate, BCMSRecord } from "@bcm-dev/bcms-vite";
import { Account, TransferTemplate, UserAccount } from "model";

/**
* @class MakeTransfer
* @classdesc Class definition for controlling what elements are rendered during make transfer workflow
/**
     * 
     * @param {account} from - the from account
     * @param {account} to -the to account
     * @param {STRING} paymentOption - payment option selected
     * @param {bcmsCurrency} amount - the payment amount
     * @param {STRING} schedule - the selected schedule
     * @param {BCMSDate} sendOn - the date selected to send transfer 
     * @param {STRING} frequency - the the frequency element should be rendered
     * @param {STRING} firstTransferDay - the selected first transfer day
     * @param {STRING} secondTransferDay - the selected second transfer day 
     * @param {bcmsDate} startDate - the selected start date
     * @param {bcmsDate} estimatedDeliveryDate - the estimated delivery date
     * @param {STRING} duration - the selected duration
     * @param {bcmsDate} endDate - the selected end date
     * @param {STRING} numberOfTransfers - the selected number of transfers for duration of transfer
     * @param {STRING} description - the entered description for transfer
     * @param {BOOLEAN} sendEmail -  if send email has been selected
     */

export default class MakeTransfer {
    _template;
    _templateOnly;
    _fromTemplateAccount;
    _from;
    _toTemplateAccount
    _to;
    _paymentOption;
    _amount;
    _templateMaxAmount;
    _schedule;
    _sendOn;
    _frequency;
    _firstTransferDay;
    _secondTransferDay;
    _startDate;
    _estimatedDeliveryDate;
    _duration;
    _endDate;
    _numberOfTransfers;
    _description;
    _sendEmail;
    _approveByDate;
    _createdBy;
    _id;
    _onDate;
    _status;
    _note;
    _branding;

    /**
     * 
     * Static Variables:
     * default data to return if unset; 
     */

    static template = "prompt";
    static templateOnly = false;
    static fromTemplateAccount = "5"
    static from = "0";
    static toTemplateAccount = "6"
    static to = "1";
    static paymentOption = (account)=> "Payment Due "+ (account.due !== undefined ? account.due.format():"$X.XX");
    static amount = "100";
    static templateMaxAmount = "500";
    static sendOn = 0;
    static frequency = "Monthly";
    static firstTransferDay = "1";
    static secondTransferDay = "15";
    static startDate = 0;
    static estimatedDeliveryDate = 0;
    static duration = "No End Date";
    static endDate =0;
    static numberOfTransfers = "5";
    static description = "";
    static sendEmail = true;
    static approveByDate = 0;
    static createdBy = "0";
    static id = "";
    static onDate = 0;
    static note = "n/a";

    // CONSTRUCTOR :: from:Account, to:Account, paymentOption:STRING, amount:BCMSCurrency, schedule:STRING, sendOn:bcmsDate, frequency:STRING, firstTransferDay:STRING, secondTransferDay:STRING, startDate:bcmsDate, estimatedDeliveryDate:bcmsDate, duration:STRING, endDate:bcmsDate, numberOfTransfers:STRING, description:STRING, sendEmail:STRING -> this
    constructor(from, to, paymentOption, amount, schedule, sendOn, frequency, firstTransferDay, secondTransferDay, startDate, estimatedDeliveryDate, duration, endDate, 
        numberOfTransfers, description, sendEmail, template, fromTemplateAccount, toTemplateAccount, templateMaxAmount, approveByDate, createdBy, templateOnly, id, onDate, status, note, branding) {
        this.branding = branding;

        // The schedule defaults to a branded value if no schedule is passed.
        if(branding) {
            this.schedule = schedule !== undefined ? schedule : branding.options.transfer_schedule_immediate_abi22;
        }
            
        this.template = template !== undefined ? template : "";
        this.templateOnly = templateOnly !==  undefined ? templateOnly : false;
        this.fromTemplateAccount = fromTemplateAccount !== undefined ? fromTemplateAccount : "";
        this.from = from !== undefined ? from : "";
        this.toTemplateAccount = toTemplateAccount !== undefined ? toTemplateAccount : "";
        this.to = to !== undefined ? to : "";
        this.paymentOption = paymentOption !== undefined ? paymentOption : "";
        this.amount = amount !== undefined ? amount : "";
        this.templateMaxAmount = templateMaxAmount !== undefined ? templateMaxAmount : "";
        this.sendOn = sendOn !== undefined ? sendOn : "";
        this.frequency = frequency !== undefined ? frequency : "Monthly";
        this.firstTransferDay = firstTransferDay !== undefined ? firstTransferDay : "";
        this.secondTransferDay = secondTransferDay !== undefined ? secondTransferDay : "";
        this.startDate = startDate !== undefined ? startDate : "";
        this.estimatedDeliveryDate = estimatedDeliveryDate !== undefined ? estimatedDeliveryDate : "";
        this.duration = duration !== undefined ? duration : "No End Date";
        this.endDate = endDate !== undefined ? endDate : "";
        this.numberOfTransfers = numberOfTransfers !== undefined ? numberOfTransfers : "";
        this.description = description !== undefined ? description : "";
        this.sendEmail = sendEmail !== undefined ? sendEmail : false;
        this.approveByDate = approveByDate !== undefined ? approveByDate : "";
        this.createdBy = createdBy !== undefined ? createdBy : "0";
        this.id = id !== undefined ? id : "";
        this.onDate = onDate !== undefined ? onDate : "";
        this.status = status !== undefined ? status : "";
        this.note = note !== undefined ? note : "";
    }
    setDefaults(branding) {     
        this.template = this.template === "" ? TransferTemplate.init({ id: MakeTransfer.template, name: "Open Transfer" }) : this.template;  
        this.templateOnly = MakeTransfer.templateOnly;

        // templates are a business only feature.
        if(branding.options.show_business_accounts_abi22 === "true") {
            //this.fromTemplateAccount = this.fromTemplateAccount === "" ? Account.initByType(MakeTransfer.fromTemplateAccount) : this.fromTemplateAccount;
            //this.toTemplateAccount = this.toTemplateAccount === "" ? Account.initByType(MakeTransfer.toTemplateAccount) : this.toTemplateAccount;
            this.fromTemplateAccount = this.fromTemplateAccount === "" ? Account.initByType(Account.getDefaultBrandedAccountByType("checking", branding).id) : this.fromTemplateAccount;
            this.toTemplateAccount = this.toTemplateAccount === "" ? Account.initByType(Account.getDefaultBrandedAccountByType("savings", branding).id) : this.toTemplateAccount;
        }
        this.from = this.from === "" ? Account.getDefaultBrandedAccountByType("checking", branding) : this.from;
        this.to = this.to === "" ? Account.getDefaultBrandedAccountByType("savings", branding) : this.to;
        this.paymentOption = this.paymentOption === "" ? MakeTransfer.paymentOption(this.to) : this.paymentOption;
        
        this.schedule = this.schedule === "" ? MakeTransfer.schedule : this.schedule;
        this.sendOn = this.sendOn === "" ? BCMSDate.init().offset(MakeTransfer.sendOn) : this.sendOn;
        this.startDate = this.startDate === "" ? BCMSDate.init().offset(MakeTransfer.startDate) : this.startDate;
        
        if(this.template.id === "prompt") {
            this.amount = this.amount === "" ? BCMSCurrency.init(MakeTransfer.amount) : this.amount;
        } else {
            this.amount = this.amount === "" ? BCMSCurrency.init(this.template.defaultAmount) : this.amount;
        }

        if(this.paymentOption === "Payment Due") {
            this.amount = this.to.due;
        }
        

        if(this.paymentOption === "Entire Balance") {
           
            this.amount = this.to.currentBalance;
        }

        const isImmediateOneTimeTransfer = () => this.schedule !== "Future (One Time)" && this.schedule !== "Recurring";

        if(isImmediateOneTimeTransfer()) {
            this.onDate = BCMSDate.init().offset(MakeTransfer.onDate);
            this.status = "succeeded";
        } else if(this.schedule === "Future (One Time)") {
            this.onDate = this.sendOn;
            this.status = "scheduled";
        } else if(this.schedule === "Recurring") {
            this.onDate = this.startDate;
            this.status = "scheduled";
        }
        
        this.frequency = this.frequency === "" ? MakeTransfer.frequency : this.frequency;
        this.firstTransferDay = this.firstTransferDay === "" ? MakeTransfer.firstTransferDay : this.firstTransferDay;
        this.secondTransferDay = this.secondTransferDay === "" ? MakeTransfer.secondTransferDay : this.secondTransferDay;
        
        this.estimatedDeliveryDate = this.estimatedDeliveryDate === "" ? BCMSDate.init().offset(MakeTransfer.estimatedDeliveryDate) : this.estimatedDeliveryDate;
        this.duration = this.duration === "" ? MakeTransfer.duration : this.duration;
        this.endDate = this.endDate === "" ? BCMSDate.init().offset(MakeTransfer.endDate) : this.endDate;
        this.numberOfTransfers = this.numberOfTransfers === "" ? MakeTransfer.numberOfTransfers : this.numberOfTransfers;
        this.description = this.description === "" ? MakeTransfer.description : this.description;
        this.approveByDate = this.approveByDate === "" ? BCMSDate.init().offset(MakeTransfer.approveByDate) : this.approveByDate;
        this.createdBy = this.createdBy === "" ? UserAccount.initByID(MakeTransfer.createdBy) : this.createdBy;
        this.note = this.note === "" ? MakeTransfer.note : this.note;
        return this;
    }
    /** {VOID} - generates an object to add to model**/
    record() {
        return {
            template: this.template.id,
            templateOnly: this.templateOnly,
            account: this.from.id,
            fromTemplateAccount: this.fromTemplateAccount.id,
            from: this.from.id,
            toTemplateAccount: this.toTemplateAccount.id,
            to: this.to.id,
            paymentOption: this.paymentOption,
            amount: this.amount.amount,
            templateMaxAmount: this.templateMaxAmount.amount,
            schedule: this.schedule,
            sendOn: this.sendOn.diff(),
            frequency: this.frequency,
            firstTransferDay: this.firstTransferDay,
            secondTransferDay: this.secondTransferDay,
            startDate: this.startDate.diff(),
            estimatedDeliveryDate: this.estimatedDeliveryDate.diff(),
            duration: this.duration,
            endDate: this.endDate.diff(),
            numberOfTransfers: this.numberOfTransfers,
            description: this.description,
            sendEmail: this.sendEmail ? "true" : "false",
            approveByDate: this.approveByDate.diff(),
            createdBy: this.createdBy.id,
            onDate: this.onDate.diff(),
            status: this.status,
            note: this.note
        }
    }

    /**
     * 
     * Getters:
     * 
     */

    /** 
    * @name id
    * @instance
    * @property get {id} - returns the id for the transfer
    * @memberof MakeTransfer 
    */
    get id() {
        return this._id;
    }

    /** 
    * @name template
    * @instance
    * @property get {template} - returns the template for the transfer
    * @memberof MakeTransfer 
    */
    get template() {
        return this._template;
    }

    /** 
    * @name templateOnly
    * @instance
    * @property get { templateOnly } - returns the templateOnly for the transfer
    * @memberof MakeTransfer 
    */
    get templateOnly() {
        return this._templateOnly;
    }

    /** 
    * @name fromTemplateAccount
    * @instance
    * @property get {account} - returns the fromTemplateAccount account
    * @memberof MakeTransfer 
    */
    get fromTemplateAccount() {
        return this._fromTemplateAccount;
    }

    /** 
    * @name from
    * @instance
    * @property get {account} - returns the from account
    * @memberof MakeTransfer 
    */
    get from() {
        return this._from;
    }

    /** 
    * @name toTemplateAccount
    * @instance
    * @property get {account} - returns the toTemplateAccount account
    * @memberof MakeTransfer 
    */
    get toTemplateAccount() {
        return this._toTemplateAccount;
    }

    /** 
     * @name to
     * @instance
     * @property get {account} - returns the to account
     * @memberof MakeTransfer 
     */
    get to() {
        return this._to;
    }

    /** 
     * @name paymentOption
     * @instance
     * @property get {STRING} - returns payment option
     * @memberof MakeTransfer 
     */
    get paymentOption() {
        return this._paymentOption;
    }

    /** 
     * @name amount
     * @instance
     * @property get {BCMSCurrency} - returns payment amount
     * @memberof MakeTransfer 
     */
    get amount() {
        return this._amount;
    }

    /** 
     * @name templateMaxAmount
     * @instance
     * @property get {BCMSCurrency} - returns payment amount
     * @memberof MakeTransfer 
     */
    get templateMaxAmount() {
        return this._templateMaxAmount;
    }

    /** 
     * @name schedule
     * @instance
     * @property get {STRING} - returns selected schedule
     * @memberof MakeTransfer 
     */
    get schedule() {
        return this._schedule;
    }

    /** 
     * @name sendOn
     * @instance
     * @property get {bcmsDate} - returns sendOn date
     * @memberof MakeTransfer 
     */
    get sendOn() {
        return this._sendOn;
    }

    /** 
    * @name frequency
    * @instance
    * @property get {STRING} - returns frequency
    * @memberof MakeTransfer 
    */
    get frequency() {
        return this._frequency;
    }

    /** 
    * @name firstTransferDay
    * @instance
    * @property get {STRING} - returns firstTransferDay
    * @memberof MakeTransfer 
    */
    get firstTransferDay() {
        return this._firstTransferDay;
    }

    /** 
    * @name secondTransferDay
    * @instance
    * @property get {STRING} - returns secondTransferDay
    * @memberof MakeTransfer 
    */
    get secondTransferDay() {
        return this._secondTransferDay;
    }

    /** 
    * @name startDate
    * @instance
    * @property get {bcmsDate} - returns startDate
    * @memberof MakeTransfer 
    */
    get startDate() {
        return this._startDate;
    }

    /** 
    * @name estimatedDeliveryDate
    * @instance
    * @property get {bcmsDate} - returns estimatedDeliveryDate
    * @memberof MakeTransfer 
    */
    get estimatedDeliveryDate() {
        return this._estimatedDeliveryDate;
    }

    /** 
    * @name duration
    * @instance
    * @property get {STRING} - returns duration
    * @memberof MakeTransfer 
    */
    get duration() {
        return this._duration;
    }

    /** 
    * @name endDate
    * @instance
    * @property get {bcmsDate} - returns endDate
    * @memberof MakeTransfer 
    */
    get endDate() {
        return this._endDate;
    }

    /** 
    * @name numberOfTransfers
    * @instance
    * @property get {STRING} - returns numberOfTransfers
    * @memberof MakeTransfer 
    */
    get numberOfTransfers() {
        return this._numberOfTransfers;
    }

    /** 
    * @name description
    * @instance
    * @property get {STRING} - returns description
    * @memberof MakeTransfer 
    */
    get description() {
        return this._description
    }

    /** 
    * @name sendEmail
    * @instance
    * @property get {STRING} - returns sendEmail
    * @memberof MakeTransfer 
    */
    get sendEmail() {
        return this._sendEmail;
    }

    /** 
    * @name approveByDate
    * @instance
    * @property get {STRING} - returns approveByDate
    * @memberof MakeTransfer 
    */
    get approveByDate() {
        return this._approveByDate;
    }

    /** 
    * @name createdBy
    * @instance
    * @property get {STRING} - returns createdBy
    * @memberof MakeTransfer 
    */
    get createdBy() {
        return this._createdBy;
    }

    /** 
    * @name onDate
    * @instance
    * @property get {STRING} - returns onDate
    * @memberof MakeTransfer 
    */
    get onDate() {
        return this._onDate;
    }

    /** 
    * @name status
    * @instance
    * @property get {STRING} - returns status
    * @memberof MakeTransfer 
    */
    get status() {
        return this._status;
    }

    /**
     * 
     * Setters:
     * 
     */

    /** 
    * @name id
    * @instance
    * @property get {id} - returns the id for the transfer
    * @memberof MakeTransfer 
    */
    set id(value) {
        return this._id = value;
    }

    /** 
    * @name template
    * @instance
    * @property set {TransferTemplate|STRING} - sets the template value as a "TransferTemplate" or an empty string.
    * @memberof MakeTransfer 
    */
    set template(value) {
        if (value instanceof BCMSRecord || value === "") {
            this._template = value;
        }else if (isNaN(value) === false && value !== "") {
            this._template = TransferTemplate.initByID(value);
        } else if(value === "prompt") {
            this._template = TransferTemplate.init({ id: "prompt", name: "Open Transfer" });
        }
    }

    /** 
    * @name templateOnly
    * @instance
    * @property set {BOOLEAN} - sets the templateOnly value.
    * @memberof MakeTransfer 
    */
    set templateOnly(value) {
        //if (value instanceof Boolean) {
            this._templateOnly = value;
        //}
    }

    /** 
    * @name fromTemplateAccount
    * @instance
    * @property set {account|STRING} - sets the fromTemplateAccount of a Template or empty string only
    * @memberof MakeTransfer 
    */
    set fromTemplateAccount(value) {
        if (value instanceof BCMSRecord || value === "") {
            this._fromTemplateAccount = value;
        }else if (isNaN(value) === false && value !== "") {
            this._fromTemplateAccount = Account.init(value)
        }
    }

    /** 
    * @name from
    * @instance
    * @property set {account|STRING} - sets the to to an Account or empty string only
    * @memberof MakeTransfer 
    */
    set from(value) {
        if (value instanceof BCMSRecord || value === "") {
            this._from = value;
        }else if (isNaN(value) === false && value !== "") {
            this._from = Account.initByType(value)
        }
    }

    /** 
    * @name toTemplateAccount
    * @instance
    * @property set {account|STRING} - sets the fromTemplateAccount of a Template or empty string only
    * @memberof MakeTransfer 
    */
    set toTemplateAccount(value) {
        if (value instanceof BCMSRecord || value === "") {
            this._toTemplateAccount = value;
        }else if (isNaN(value) === false && value !== "") {
            this._toTemplateAccount = Account.init(value)
        }
    }

    /** 
     * @name to
     * @instance
     * @property set {account|STRING} - sets the to to an Account or empty string only
     * @memberof MakeTransfer 
     */
    set to(value) {
        if (value instanceof BCMSRecord || value === "") {
            this._to = value;
        }else if (isNaN(value) === false && value !== "") {
            this._to = Account.initByType(value)
        }
    }

    /** 
     * @name paymentOption
     * @instance
     * @property set {STRING} - sets the paymentOption
     * @memberof MakeTransfer 
     */
    set paymentOption(value) {
        if (value !== undefined) {
            this._paymentOption = value;
        }

    }

    /** 
     * @name amount
     * @instance
     * @property set {bcmsCurrency} - sets amount field to instance of BCMSCurrency if none is provided 
     * @memberof MakeTransfer 
     */
    set amount(value) {

        if (isNaN(value) === false && value !== "") {
            this._amount = BCMSCurrency.init(value);
        }else if(value === ""){
            this._amount = ""; 
        }else if(value instanceof BCMSCurrency){
            this._amount = value;
        }

    }

    /** 
     * @name templateMaxAmount
     * @instance
     * @property set {bcmsCurrency} - sets templateMaxAmount field to instance of BCMSCurrency if none is provided 
     * @memberof MakeTransfer 
     */
    set templateMaxAmount(value) {
        if(isNaN(value) === false && value !== "") {
            this._templateMaxAmount = BCMSCurrency.init(value);
        } else if(value === "") {
            this._templateMaxAmount = ""; 
        } else if(value instanceof BCMSCurrency) {
            this._templateMaxAmount = value;
        }
    }

    /** 
     * @name schedule
     * @instance
     * @property set {STRING} - sets the schedule 
     * @memberof MakeTransfer 
     */
    set schedule(value) {
        if (value !== undefined) {
            this._schedule = value;
        }
    }

    /** 
     * @name sendOn
     * @instance
     * @property set {bcmsDate} - sets the sendOn only if instance of BCMSDate
     * @memberof MakeTransfer 
     * @note estimatedDeliveryDate is set to be the same as the sendOn date if set
     */
    set sendOn(value) {
        if ( value instanceof BCMSDate) {
            this._sendOn = value ;
        }else if(value === ""){
            this._sendOn = value;
        }
        
    }

    /** 
    * @name frequency
    * @instance
    * @property set {STRING} - sets the frequency 
    * @memberof MakeTransfer 
    */
    set frequency(value) {
        if (value !== undefined) {
            this._frequency = value;
        }
    }

    /** 
    * @name firstTransferDay
    * @instance
    * @property set {STRING} - sets the firstTransferDay 
    * @memberof MakeTransfer 
    */
    set firstTransferDay(value) {
        if (value !== undefined) {
            this._firstTransferDay = value;
        }
    }

    /** 
    * @name secondTransferDay
    * @instance
    * @property set {STRING} - sets the secondTransferDay 
    * @memberof MakeTransfer 
    */
    set secondTransferDay(value) {
        if (value !== undefined) {
            this._secondTransferDay = value;
        }
    }

    /** 
    * @name startDate
    * @instance
    * @property set {bcmsDate}  - sets the startDate to instance of BCMSDate
    *  @note estimatedDeliveryDate is set to be the same as the startDate date if set
    * @memberof MakeTransfer 
    */
    set startDate(value) {
        if ( value instanceof BCMSDate) {
            this._startDate = value ;
        }else if(value === ""){
            this._startDate = value;
        }
      
    }

    /** 
    * @name estimatedDeliveryDate
    * @instance
    * @property set {bcmsDate} - sets the estimatedDeliverDate to instance of BCMSDate
    * @memberof MakeTransfer 
    */
    set estimatedDeliveryDate(value) {       
        if ( value instanceof BCMSDate) {
            this._estimatedDeliveryDate = value ;
        }else if(value === ""){
            this._estimatedDeliveryDate = value;
        }
    }

    /** 
    * @name duration
    * @instance
    * @property set {STRING} - sets the duration 
    * @memberof MakeTransfer 
    */
    set duration(value) {
        if (value !== undefined) {
            this._duration = value;
        }
    }

    /** 
    * @name endDate
    * @instance
    * @property set {bcmsDate} - sets the endDate to instance of BCMSDate
    * @memberof MakeTransfer 
    */
    set endDate(value) {
        if ( value instanceof BCMSDate) {
            this._endDate = value ;
        }else if(value === ""){
            this._endDate = value;
        }
    }

    /** 
    * @name numberOfTransfers
    * @instance
    * @property set {STRING} - sets the numberOfTransfers 
    * @memberof MakeTransfer 
    */
    set numberOfTransfers(value) {
        if (value !== undefined) {
            this._numberOfTransfers = value;
        }
    }
    /** 
    * @name description
    * @instance
    * @property set {STRING} - sets the description 
    * @memberof MakeTransfer 
    */
    set description(value) {
            if (value !== undefined) {
                this._description = value;
            }
        }

    /** 
    * @name sendEmail
    * @instance
    * @property set {STRING} - sets the sendEmail 
    * @memberof MakeTransfer 
    */
    set sendEmail(value) {
            this._sendEmail = value !== true ? false : true;
        }

    /** 
    * @name approveByDate
    * @instance
    * @property set {bcmsDate} - sets the approveByDate to instance of BCMSDate
    * @memberof MakeTransfer 
    */
    set approveByDate(value) {
        if (value instanceof BCMSDate) {
            this._approveByDate = value ;
        } else if(value === ""){
            this._approveByDate = value;
        }
    }

    /** 
    * @name createdBy
    * @instance
    * @property set {STRING} - sets the createdBy 
    * @memberof MakeTransfer 
    */
    set createdBy(value) {
        if (value instanceof BCMSRecord || value === "") {
            this._createdBy = value;
        }else if (isNaN(value) === false && value !== "") {
            this._createdBy = UserAccount.initByID(value);
        }
    }

    /** 
     * @name onDate
     * @instance
     * @property set {bcmsDate} - sets the onDate
     * @memberof MakeTransfer 
     */
    set onDate(value) {
        if(value instanceof BCMSDate) {
            this._onDate = value ;
        } else if(value === "") {
            this._onDate = value;
        }
    }

    /** 
     * @name status
     * @instance
     * @property set {bcmsDate} - sets the onDate
     * @memberof MakeTransfer 
     */
    set status(value) {
        this._status = value;
    }

    /**
     * @name init
     * @static
     * @param {VOID|makeTransfer} init - Static factory method for creating an instance of MakeTransfer. 
     * @memberof MakeTransfer
     */
    static init(makeTransfer, branding) {
        makeTransfer = makeTransfer ?? {};

        return new MakeTransfer(makeTransfer.from, makeTransfer.to, makeTransfer.paymentOption, 
        makeTransfer.amount, makeTransfer.schedule, makeTransfer.sendOn, makeTransfer.frequency, 
        makeTransfer.firstTransferDay, makeTransfer.secondTransferDay, makeTransfer.startDate, 
        makeTransfer.estimatedDeliveryDate, makeTransfer.duration, makeTransfer.endDate, 
        makeTransfer.numberOfTransfers, makeTransfer.description, makeTransfer.sendEmail,
        makeTransfer.template, makeTransfer.fromTemplateAccount, makeTransfer.toTemplateAccount,
        makeTransfer.templateMaxAmount, makeTransfer.approveByDate, makeTransfer.createdBy,
        makeTransfer.templateOnly, makeTransfer.id, makeTransfer.onDate, makeTransfer.status,
        makeTransfer.note, branding);
    }    
}